/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Connection Configuration Step Component
 * Matches Copilot1 pattern for connection setup
 */

import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '../../../../services/serviceTypes';
import type { StepFormMessage } from '../../data-layer/types';
import { setSavedStepData } from '../../data-store/copilotSlice';
import { FORM_RESPONSE_TEXTS } from '../../services/responseTextService';
import type { ParsedField } from '../../utils/templateParsers';
import ExpandableForm from '../ExpandableForm/ExpandableForm';
import ResponseWithForm from '../ResponseWithForm/ResponseWithForm';
import StepRenderer from '../StepRenderer/StepRenderer';

import './StepComponents.css';

// Extended type for fields with validation
type ParsedFieldWithValidation = ParsedField & {
  hasError?: boolean;
  errorMessage?: string;
};

interface ConnectionConfigStepProps {
  message: StepFormMessage;
  onFormSubmit: (
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ) => void;
}

/**
 * Connection Configuration Step - Connection Setup
 */
function ConnectionConfigStep({
  message,
  onFormSubmit,
}: ConnectionConfigStepProps) {
  const dispatch = useDispatch();
  const [validationFields, setValidationFields] = useState<
    ParsedFieldWithValidation[]
  >([]);

  // Get persisted form data from Redux for validation
  const persistedFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData.Connection ?? {}
  );

  const isEditModeViaParam = useSelector(
    (state: RootState) => state.copilot.ui.endpointKey
  );

  const isCompletedCheck = useSelector(
    (state: RootState) => state.copilot.navigation.completedSteps ?? {}
  );

  const btnlabel = isCompletedCheck.includes(message.stepName ?? '');

  const validateAndContinue = useCallback(() => {
    const parsedFields = message.fields as ParsedField[];
    const requiredFields = parsedFields.filter((f) => f.required);

    // Generate field-level validation results
    const fieldValidationResults: ParsedFieldWithValidation[] =
      requiredFields.map((field) => {
        const value = String(
          persistedFormData[field.attributeName || ''] || ''
        ).trim();
        const isEmpty = value === '';

        let errorMessage = '';

        if (isEmpty) {
          // Specific error messages based on field type
          if (
            field.attributeName.toLowerCase().includes('username') ||
            field.attributeName.toLowerCase().includes('password')
          ) {
            errorMessage = 'This is required';
          } else {
            errorMessage = 'This is required';
          }
        }

        return {
          ...field,
          hasError: isEmpty,
          errorMessage,
        };
      });

    // Check if any field has errors
    const hasErrors = fieldValidationResults.some((field) => field.hasError);
    const isValid = !hasErrors;

    // Update validation state for rendering
    setValidationFields(fieldValidationResults);

    if (isValid) {
      // Save to savedStepData on successful validation
      dispatch(
        setSavedStepData({
          stepName: message.stepName ?? 'Connection',
          formData: persistedFormData,
        })
      );
      // Clear validation errors on success
      setValidationFields([]);
    }

    onFormSubmit(message.stepName ?? 'Connection', persistedFormData, isValid);

    return isValid;
  }, [
    message.fields,
    message.stepName,
    persistedFormData,
    onFormSubmit,
    dispatch,
  ]);

  const formContent = (
    <ExpandableForm
      stepName={message.stepName ?? 'Basic'}
      heading={message.heading}
      buttonLabel={btnlabel ? 'Update' : message.buttonLabel}
      buttonkind='outlined'
      onSave={validateAndContinue}>
      <StepRenderer
        stepName='Connection'
        className='Copilot-StepContent'
        validationFields={validationFields}
      />
    </ExpandableForm>
  );

  return !isEditModeViaParam ? (
    <ResponseWithForm responseText={FORM_RESPONSE_TEXTS.CONNECTION}>
      {formContent}
    </ResponseWithForm>
  ) : (
    formContent
  );
}

export default ConnectionConfigStep;
